import type { FC } from "react";
import { useMemo } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";

import type { DeviceAllowedCodeLength } from "@jti/fe-common";
import { DEVICE_CODE_REGEX, FORMATTED_CODE_POSSIBLE_LENGTH, registrationCodeFormatter } from "@jti/fe-common";

import styles from "./RegisterDeviceInput.module.scss";

export type RegisterDeviceInputProps = {
  fieldName: string;
  isInputDisabled?: boolean;
  isDeviceRegistrationError?: boolean;
  deviceRegistrationErrorText?: string;
  onCleanErrors?: () => void;
  requiredCodeLength: DeviceAllowedCodeLength[];
  isInputHelperInfoTextVisible?: boolean;
};

export const RegisterDeviceInput: FC<RegisterDeviceInputProps> = ({
  fieldName = "registrationCode",
  requiredCodeLength,
  isInputDisabled = false,
  isDeviceRegistrationError,
  deviceRegistrationErrorText,
  onCleanErrors = () => {},
  isInputHelperInfoTextVisible = true,
}) => {
  const { __ } = useTranslations();

  const { control } = useFormContext();

  const { minCodeLength, maxCodeLength } = useMemo(() => {
    const minRequiredLength = Math.min(...requiredCodeLength) as DeviceAllowedCodeLength;
    const maxRequiredLength = Math.max(...requiredCodeLength) as DeviceAllowedCodeLength;

    return {
      minCodeLength: FORMATTED_CODE_POSSIBLE_LENGTH[minRequiredLength],
      maxCodeLength: FORMATTED_CODE_POSSIBLE_LENGTH[maxRequiredLength],
    };
  }, [requiredCodeLength]);

  const formatDeviceCode = (deviceCode: string) => registrationCodeFormatter(deviceCode, " ", requiredCodeLength);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextInput
          {...field}
          onChange={(e) => {
            field.onChange(e);
            onCleanErrors();
          }}
          disabled={isInputDisabled}
          formatter={formatDeviceCode}
          containerClassName={styles.input}
          data-testid="registrationCodeTextInput"
          isError={isDeviceRegistrationError || !!error}
          errorText={deviceRegistrationErrorText || error?.message}
          label={__("profile.devices.register_new.registration_code")}
          helperText={
            isInputHelperInfoTextVisible
              ? __("profile.devices.register_new.dialog.registration_code_validation.format") ||
                __("profile.devices.register_new.registration_code_placeholder")
              : undefined
          }
          placeholder={
            __("profile.devices.register_new.dialog.registration_code_placeholder") ||
            __("profile.devices.register_new.registration_code_placeholder")
          }
        />
      )}
      rules={{
        required: __("profile.devices.register_new.registration_code_validation.required"),
        pattern: {
          value: DEVICE_CODE_REGEX,
          message: __("profile.devices.register_new.registration_code_validation.format_error"),
        },
        maxLength: {
          value: maxCodeLength,
          message: __("profile.devices.register_new.registration_code_validation.format_error"),
        },
        minLength: {
          value: minCodeLength,
          message: __("profile.devices.register_new.registration_code_validation.format_error"),
        },
      }}
    />
  );
};
